import type { IntlShape } from 'react-intl';
import { defineMessages } from 'react-intl';

// Formatting helpers for the admin grace period (48h cooldown before a newly
// granted protected role, e.g. Workspace Admin, becomes usable). Shared by the
// Team table tooltip, the edit tray banner and the protected-role warnings.

const MINUTE_MS = 60 * 1000;
const MINUTES_PER_HOUR = 60;

const messages = defineMessages({
  hours: {
    id: 'Team.gracePeriodFormat.hours',
    defaultMessage: '{count, plural, one {# hour} other {# hours}}',
    description:
      'Grace period length in hours, e.g. "48 hours". Used inside "Newly granted Admin access will start after a delay of 48 hours."',
  },
  minutes: {
    id: 'Team.gracePeriodFormat.minutes',
    defaultMessage: '{count, plural, one {# minute} other {# minutes}}',
    description:
      'Grace period length in minutes, e.g. "30 minutes". Used inside "Newly granted Admin access will start after a delay of 30 minutes."',
  },
  hoursAndMinutes: {
    id: 'Team.gracePeriodFormat.hoursAndMinutes',
    defaultMessage: '{hours} {minutes}',
    description:
      'Grace period length made of hours and minutes, e.g. "1 hour 30 minutes". {hours} is e.g. "1 hour", {minutes} is e.g. "30 minutes".',
  },
});

/** Whether a role that takes effect at `effectiveAt` is still pending at `now`. */
export function isPending(effectiveAt: Date, now: Date): boolean {
  return effectiveAt.getTime() > now.getTime();
}

/**
 * Grace period length as words, e.g. "48 hours", "30 minutes" or
 * "1 hour 30 minutes". Rounds up to whole minutes so a short configured period
 * never reads as "0 hours".
 */
export function formatGracePeriodDuration(intl: IntlShape, gracePeriodMs: number): string {
  const totalMinutes = Math.max(1, Math.ceil(gracePeriodMs / MINUTE_MS));
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  if (hours === 0) return intl.formatMessage(messages.minutes, { count: minutes });
  if (minutes === 0) return intl.formatMessage(messages.hours, { count: hours });
  return intl.formatMessage(messages.hoursAndMinutes, {
    hours: intl.formatMessage(messages.hours, { count: hours }),
    minutes: intl.formatMessage(messages.minutes, { count: minutes }),
  });
}

/**
 * Absolute start time without the year, e.g. "Sep 27, 10:00 AM PDT", in the
 * viewer's locale and time zone.
 */
export function formatEffectiveAt(intl: IntlShape, effectiveAt: Date): string {
  return intl.formatDate(effectiveAt, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}
